// Generated by Claude Code
import { StepperButton } from '#components/domain/operations/parts/shared/stepper-button.tsx'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface PartQuantityStepperProps {
  /** Quantity as a raw input string; blank counts as 0. */
  value: string
  onChange: (value: string) => void
  /** Deferred write: fires on blur, Enter and each −/+ click with the settled value. */
  onCommit?: (value: string) => void
  disabled?: boolean
  placeholder?: string
  /** Native tooltip on the whole control (e.g. the not-authorized reason). */
  title?: string
  /** Upper bound the control refuses to step or settle past; unbounded when omitted. */
  max?: number
  /** Accessible name for the number input, for controls with no visible label of their own. */
  ariaLabel?: string
}

/** Numeric input flanked by −/+ buttons stepping by 1, stopping at 0 and at `max`. */
export function PartQuantityStepper({ value, onChange, onCommit, disabled, placeholder, title, ariaLabel, max }: PartQuantityStepperProps) {
  // The `parts` namespace is loaded lazily and is usually NOT in memory where this stepper is
  // mounted from another domain's screen — the work-order detail's Parts section, its edit drawer,
  // the resolution modal. With react-i18next's default Suspense mode that missing namespace
  // suspends the stepper, which bubbles to the router's boundary and tears down + remounts the
  // whole screen, reading as an unexplained page refresh the moment Edit is pressed.
  // `useSuspense: false` renders immediately (the two −/+ aria-labels fall back to their key, then
  // re-render once the namespace resolves) so the host screen is never unmounted.
  const { t } = useTranslation('parts', { useSuspense: false })

  const parsed = Number(value.trim())
  const amount = Number.isFinite(parsed) ? parsed : 0

  const step = (delta: number) => {
    const stepped = amount + delta
    const next = String(Math.max(0, max !== undefined ? Math.min(max, stepped) : stepped))
    onChange(next)
    onCommit?.(next)
  }

  // Typing is left alone — a cap applied mid-keystroke fights the user — so an over-cap entry is
  // settled down on blur, Enter and each −/+ click instead. A blank stays blank: callers with no
  // `max` see the raw value they always did.
  const commit = (raw: string) => {
    const entered = Number(raw.trim())
    const capped = max !== undefined && Number.isFinite(entered) && entered > max ? String(max) : raw
    if (capped !== raw) onChange(capped)
    onCommit?.(capped)
  }

  return (
    <div className="d-flex align-items-center gap-2" title={title}>
      <StepperButton
        direction="decrease"
        ariaLabel={t('editDrawer.stepper.decrease')}
        disabled={disabled || amount <= 0}
        onClick={() => step(-1)}
      />
      <input
        type="number"
        min="0"
        max={max}
        step="any"
        className="form-control text-center"
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(e.currentTarget.value) }}
      />
      <StepperButton
        direction="increase"
        ariaLabel={t('editDrawer.stepper.increase')}
        disabled={disabled || (max !== undefined && amount >= max)}
        onClick={() => step(1)}
      />
    </div>
  )
}

export interface PartQuantityFieldProps {
  /** The settled quantity; typed edits are held locally until they settle. */
  quantity: number
  disabled?: boolean
  /** Accessible name for the number input, for controls with no visible label of their own. */
  ariaLabel?: string
  /** Fires on blur, Enter and each −/+ click, only with a changed value above zero; awaited (a rejection included), so a host that refuses the write drops the typed number. */
  onCommit: (quantity: number) => void | Promise<void>
}

/**
 * {@link PartQuantityStepper} over a local draft, so the field can be cleared and a decimal
 * typed digit by digit; the draft is dropped once the commit settles, so anything the host
 * refuses — or that never settles above zero — falls back to `quantity`.
 */
export function PartQuantityField({ quantity, disabled, ariaLabel, onCommit }: PartQuantityFieldProps) {
  const [draft, setDraft] = useState<string | null>(null)

  return (
    <PartQuantityStepper
      value={draft ?? String(quantity)}
      ariaLabel={ariaLabel}
      disabled={disabled}
      onChange={setDraft}
      onCommit={(value) => {
        const next = Number(value.trim())
        if (Number.isFinite(next) && next > 0 && next !== quantity) void Promise.resolve(onCommit(next)).catch(() => {}).finally(() => setDraft(null))
        else setDraft(null)
      }}
    />
  )
}
